/**
 * @file Dashboard.jsx
 * @description Authenticated user dashboard with premium card design.
 *              Features:
 *              - 4-column bento grid for primary metrics (Daily Challenge, Stats, Progress, Achievements).
 *              - Interactive SVG D-ring (ProgressRing) with 3D flip for difficulty breakdown.
 *              - 365-day Activity Calendar heatmap.
 *              - Recent Submissions list with quick-solve navigation.
 *              - System Design track progress and completed designs list.
 *              - Synchronized Navbar & Footer navigation links.
 * 
 * @module components/Dashboard
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ActivityCalendar from './ActivityCalendar';
import BadgeCarousel from './BadgeCarousel';
import BadgeImage from './BadgeImage';
import { apiGet, apiPut, apiGetCached } from '../utils/apiUtils';
import { publicApiGet } from '../utils/apiUtils';
import UserAvatar from './UserAvatar';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import {
    FiCode, FiTrendingUp, FiArrowRight, FiCheckCircle, FiZap, FiLayers,
    FiBookOpen, FiX, FiCalendar, FiExternalLink, FiBarChart2, FiActivity,
    FiLoader, FiUsers, FiCheck, FiTarget, FiAward, FiUser, FiBookmark,
    FiCheckSquare, FiCodeOpen, FiBox
} from 'react-icons/fi';
import { FaFire, FaStar } from 'react-icons/fa';

/* ══════════════════════════════════════════════════════════════
 * HELPERS & UTILS
 * ══════════════════════════════════════════════════════════════ */

/**
 * Returns Tailwind text configuration for difficulty levels.
 */
const getDifficultyColor = (d) => {
    switch (d?.toLowerCase()) {
        case 'easy': return 'text-green-600';
        case 'medium': return 'text-yellow-600';
        case 'hard': return 'text-red-600';
        default: return 'text-muted-foreground';
    }
};

/**
 * Returns Tailwind background/text configuration for difficulty tags.
 */
const getDifficultyBg = (d) => {
    switch (d?.toLowerCase()) {
        case 'easy': return 'bg-white text-green-700';
        case 'medium': return 'bg-white text-yellow-700';
        case 'hard': return 'bg-white text-red-700';
        default: return 'text-gray-600';
    }
};

/**
 * Maps submission status strings to branding colors.
 */
const getStatusColor = (s) => {
    if (s.includes('Accepted')) return 'text-green-600';
    if (s.includes('Wrong Answer')) return 'text-red-600';
    if (s.includes('Time Limit')) return 'text-purple-600';
    if (s.includes('Runtime Error')) return 'text-cyan-600';
    if (s.includes('Compilation Error')) return 'text-amber-600';
    return 'text-muted-foreground';
};

/* ══════════════════════════════════════════════════════════════
 * SUB-COMPONENTS
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component ProgressRing
 * @description A dual-purpose SVG visualization.
 *              Front: half-ring (D-ring) showing overall platform progress.
 *              Back: Full ring showing the distribution of solved problems by difficulty.
 *              Synchronized with a legendary 3D-flip effect on hover.
 * 
 * @param {Object} props
 * @param {number} props.easy - Count of easy problems solved.
 * @param {number} props.medium - Count of medium problems solved.
 * @param {number} props.hard - Count of hard problems solved.
 * @param {number} props.totalSolved - Total problems solved across all difficulties.
 * @param {number} props.totalProblems - Total questions available on the platform.
 */
const ProgressRing = ({ easy, medium, hard, totalSolved, totalProblems, easyTotal, mediumTotal, hardTotal }) => {
    const [hovered, setHovered] = useState(false);
    const size = 140;
    const strokeWidth = 12;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const halfCirc = circumference / 2;

    // D-ring calculation: proportion of the top-half arc (180deg)
    const solvedPct = totalProblems > 0 ? totalSolved / totalProblems : 0;
    const dFill = halfCirc * solvedPct;

    // Legend Breakdown calculation: portions of the full 360deg circle
    const solvedSum = totalSolved || 1;
    const easyLen = circumference * (easy / solvedSum);
    const medLen = circumference * (medium / solvedSum);
    const hardLen = circumference * (hard / solvedSum);

    const cx = size / 2;
    const cy = size / 2;

    return (
        <div className="relative flex flex-col items-center cursor-pointer"
            onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
            style={{ perspective: '800px' }}>

            {/* ── 3D Flip Container ── */}
            <div style={{
                width: size, height: size,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.6s ease-in-out',
                transform: hovered ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}>
                {/* FRONT FACE: Overall Progress Half-Ring */}
                <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden' }}>
                    <svg width={size} height={size} className="transform rotate-180">
                        <circle cx={cx} cy={cy} r={radius} fill="none"
                            stroke="#e5e7eb" strokeWidth={strokeWidth}
                            strokeDasharray={`${halfCirc} ${halfCirc}`}
                        />
                        <circle cx={cx} cy={cy} r={radius} fill="none"
                            stroke="#059669" strokeWidth={strokeWidth}
                            strokeDasharray={`${dFill} ${circumference - dFill}`}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl font-bold text-foreground">{totalSolved}</span>
                        <span className="text-xs text-muted-foreground">out of</span>
                        <span className="text-sm font-semibold text-foreground">{totalProblems}</span>
                    </div>
                </div>

                {/* BACK FACE: Difficulty Specific Color Ring */}
                <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                    <svg width={size} height={size} className="transform rotate-180">
                        {totalSolved === 0 && (
                            <circle cx={cx} cy={cy} r={radius} fill="none"
                                stroke="#e5e7eb" strokeWidth={strokeWidth}
                            />
                        )}
                        <circle cx={cx} cy={cy} r={radius} fill="none"
                            stroke="#22c55e" strokeWidth={strokeWidth}
                            strokeDasharray={`${easyLen} ${circumference - easyLen}`}
                            strokeDashoffset={0}
                        />
                        <circle cx={cx} cy={cy} r={radius} fill="none"
                            stroke="#eab308" strokeWidth={strokeWidth}
                            strokeDasharray={`${medLen} ${circumference - medLen}`}
                            strokeDashoffset={-easyLen}
                        />
                        <circle cx={cx} cy={cy} r={radius} fill="none"
                            stroke="#ef4444" strokeWidth={strokeWidth}
                            strokeDasharray={`${hardLen} ${circumference - hardLen}`}
                            strokeDashoffset={-(easyLen + medLen)}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl font-bold text-foreground">{totalSolved}</span>
                        <span className="text-xs text-muted-foreground">out of</span>
                        <span className="text-sm font-semibold text-foreground">{totalProblems}</span>
                    </div>
                </div>
            </div>

            {/* ── Legendary Flipping Legend ── */}
            <div className="mt-4 text-center relative min-h-[1.25rem] w-full">
                <div style={{
                    position: 'absolute', inset: 0,
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.6s ease-in-out',
                    transform: hovered ? 'rotateY(180deg)' : 'rotateY(0deg)',
                }}>
                    {/* Front View: % Completion */}
                    <div className="absolute inset-0 flex items-center justify-center" style={{ backfaceVisibility: 'hidden' }}>
                        <span className="text-sm text-emerald-600 font-medium whitespace-nowrap">{Math.round(solvedPct * 100)}% Complete</span>
                    </div>
                    {/* Back View: Difficulty breakdown colors */}
                    <div className="absolute inset-0 flex items-center justify-center gap-4" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                        <div className="flex items-center gap-1.5 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-xs text-muted-foreground font-medium">{easy} <span className="hidden sm:inline">Easy</span></span></div>
                        <div className="flex items-center gap-1.5 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-yellow-500" /><span className="text-xs text-muted-foreground font-medium">{medium} <span className="hidden sm:inline">Med</span></span></div>
                        <div className="flex items-center gap-1.5 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-xs text-muted-foreground font-medium">{hard} <span className="hidden sm:inline">Hard</span></span></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component Dashboard
 * @description The unified mission control for an authenticated user.
 *              Retrieves and displays:
 *              - Core platform stats (streaks, solves).
 *              - Gamified progress (badges and progress ring).
 *              - Historical activity (heatmap and recent history).
 *              - Educational progress (DSA tracks and System Design track).
 * 
 * @param {Object} props
 * @param {Function} props.navigate - Reach Router navigation function.
 * @returns {JSX.Element} The rendered dashboard page.
 */
const Dashboard = ({ navigate }) => {
    // Config toggles
    const IS_SYSTEM_DESIGN_COMING_SOON = false;

    // State: UI Status
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State: DSA Data
    const [userStats, setUserStats] = useState(null);
    const [recentSubmissions, setRecentSubmissions] = useState([]);
    const [todaySolved, setTodaySolved] = useState(0);
    const [dailyProblem, setDailyProblem] = useState(null);
    const [dailyStreak, setDailyStreak] = useState({ currentStreak: 0, solvedToday: false });
    const [submissionStreak, setSubmissionStreak] = useState({ currentStreak: 0, longestStreak: 0 });

    // State: Gamification Data
    const [pendingBadges, setPendingBadges] = useState([]);
    const [showDashboardBadgeNotification, setShowDashboardBadgeNotification] = useState(false);

    // State: System Design Data
    const [systemDesignStats, setSystemDesignStats] = useState(null);
    const [systemDesignProgressBreakdown, setSystemDesignProgressBreakdown] = useState(null);

    // State: Batch/Classroom Data
    const [hasBatches, setHasBatches] = useState(false);

    const { isAuthenticated, user, logout, token } = useAuth();

    // Set browser tab title on mount
    useEffect(() => { document.title = 'SimpleCode | Dashboard'; }, []);

    /**
     * Fetches all dashboard data in a single consolidated request.
     * Uses apiGetCached to minimize network traffic on frequent navigations.
     */
    const fetchDashboardSummary = async (u) => {
        if (!u?.id) return;
        try {
            setError(null);
            // Force clear cache for this endpoint to ensure the fix is visible immediately
            localStorage.removeItem(`api_cache_/dashboard/summary/${u.id}`);
            
            // Use cached version (5 min TTL) to avoid hitting backend on every tab switch
            const response = await apiGetCached(`/dashboard/summary/${u.id}`, 0);
            
            if (response.ok) {
                const data = await response.json();
                console.log('[Dashboard] Debug - hasBatches:', data.hasBatches);
                console.log('[Dashboard] Received summary data:', data);
                
                // 1. DSA Data
                setUserStats(data.userStats);
                setRecentSubmissions(data.recentSubmissions);
                setSubmissionStreak(data.submissionStreak);
                
                // 2. Daily Challenge Data
                setDailyProblem(data.dailyProblem);
                setDailyStreak(data.dailyStreak);
                
                const today = new Date().toDateString();
                setTodaySolved((data.recentSubmissions?.filter(s =>
                    new Date(s.created_at).toDateString() === today && s.status === 'Accepted'
                ) || []).length);

                // 3. System Design Data
                setSystemDesignStats(data.systemDesign.stats);
                const topics = data.systemDesign.topics;
                const pMap = data.systemDesign.progress;

                const regular = topics.filter(t => t.type === 'regular').map(t => {
                    const c = t.content || [];
                    const total = c.length;
                    const completed = c.filter(x => pMap[x.id]?.completed).length;
                    return {
                        id: t.id,
                        title: t.title,
                        total,
                        completed,
                        percentage: total > 0 ? Math.round((completed / total) * 100) : 0
                    };
                });

                const interview = topics.find(t => t.type === 'interview');
                if (interview) {
                    const ic = interview.content || [];
                    const ci = ic.filter(x => pMap[x.id]?.completed);
                    setSystemDesignProgressBreakdown({
                        regularTopics: regular,
                        interviewDesigns: {
                            total: ic.length,
                            completed: ci.length,
                            completedList: ci.map(x => ({ id: x.id, title: x.title }))
                        }
                    });
                }

                // 4. Classroom & Badges
                setHasBatches(data.hasBatches);
                if (data.pendingBadges?.length > 0) {
                    setPendingBadges(data.pendingBadges);
                    setShowDashboardBadgeNotification(true);
                }
            } else {
                setError('Failed to load dashboard summary');
            }
        } catch (e) {
            console.error('Error fetching dashboard summary:', e);
            setError('Failed to load');
        }
    };

    /** Marks earned badges as seen to prevent repeat notifications. */
    const markBadgesAsSeen = async () => {
        if (!isAuthenticated || !user?.id) return;
        try {
            await apiPut(`/badges/notifications/${user.id}/seen`);
        } catch (e) {
            console.error(e);
        }
    };

    const closeDashboardBadgeNotification = () => {
        setShowDashboardBadgeNotification(false);
        markBadgesAsSeen();
        setPendingBadges([]);
    };

    /* ── Effects ── */

    // Primary data fetch on load/mount
    useEffect(() => {
        const f = async () => {
            if (isAuthenticated && user?.id) {
                setLoading(true);
                await fetchDashboardSummary(user);
                setLoading(false);
            }
        };
        f();
    }, [isAuthenticated, user]);

    // Visibility Listener: Refresh daily challenge data when window regains focus
    useEffect(() => {
        const v = () => { if (!document.hidden && isAuthenticated) fetchDashboardSummary(user); };
        const f = () => { if (isAuthenticated) fetchDashboardSummary(user); };
        document.addEventListener('visibilitychange', v);
        window.addEventListener('focus', f);
        return () => {
            document.removeEventListener('visibilitychange', v);
            window.removeEventListener('focus', f);
        };
    }, [isAuthenticated, user, token]);

    if (loading) return <DashboardSkeleton navigate={navigate} />;

    const overallProgress = userStats?.problemStats?.total
        ? Math.round((userStats.solvedStats.total / userStats.problemStats.total) * 100)
        : 0;

    // Standardized links for Navbar & Footer
    const dashboardNavLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard") },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ];

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    /* ══════════════════════════════════════════════════════════════
     * RENDER
     * ══════════════════════════════════════════════════════════════ */
    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={dashboardNavLinks} />

            <main className="flex-1 py-16">

                {/* ── Dashboard Header / Command Bar ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-3">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h1 className="text-2xl sm:text-3xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8 text-left">
                                Welcome Back, <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">{user?.name?.split(' ')[0] || user?.username || 'Coder'}</span>
                            </h1>

                            {/* Classroom Banner — inline */}
                            {hasBatches && (
                                <div className="bg-emerald-600 rounded p-3 relative overflow-hidden group cursor-pointer flex-shrink-0" onClick={() => navigate('/batches')}>
                                    <div className="absolute right-[-10px] top-[-10px] opacity-30 transform -rotate-12 pointer-events-none">
                                        <FiBookOpen className="w-24 h-24 text-emerald-100" />
                                    </div>
                                    <div className="relative z-10 flex items-center gap-4 text-left">
                                        <div>
                                            <span className="font-semibold text-white text-md">My Classroom</span>
                                            <div className="flex items-center gap-2 mt-0.5 text-emerald-100 text-xs">
                                                <span>Batches and Assessments</span>
                                            </div>
                                        </div>
                                        <button className="px-3 py-1.5 rounded bg-white text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition-colors whitespace-nowrap">
                                            Access Now →
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* ── Dashboard Grid Content ── */}
                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-8 relative">

                    {/* Section: Data Structures & Algorithms */}
                    <div className="space-y-6 mb-12">
                        <h2 className="text-xl font-bold text-foreground flex items-center gap-2 underline decoration-emerald-600 underline-offset-4">
                            <FiCode className="text-emerald-600 w-5 h-5" />
                            Data Structures & Algorithms
                        </h2>

                        {/* Top Metrics Row: Bento Grid Style */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">

                            {/* CARD: Today's Daily Challenge */}
                            <div className="group bg-white border-l-4 border-emerald-600 rounded p-5 shadow-sm transition-all duration-300">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <FiCalendar className="text-emerald-600 w-4 h-4" />
                                        <h2 className="text-md font-semibold text-foreground mt-0.5">Today's Daily Challenge</h2>
                                    </div>
                                    {dailyStreak.solvedToday && <FiCheck className="bg-emerald-600 text-white p-0.5 rounded-full w-5 h-5 flex-shrink-0" />}
                                </div>
                                {dailyProblem ? (
                                    <>
                                        <div className="bg-emerald-600 rounded p-3 mb-3 relative overflow-hidden flex flex-col justify-between">
                                            <div className="absolute right-[-10px] top-[-10px] opacity-20 transform -rotate-12 pointer-events-none">
                                                <FiCode className="w-24 h-24 text-emerald-100" />
                                            </div>
                                            <button onClick={() => navigate(`/problems/${dailyProblem.question_id}`)}
                                                className="w-full text-left transition-colors cursor-pointer relative z-10">
                                                <span className="font-medium text-white text-sm line-clamp-2 leading-relaxed">{dailyProblem.questions.title}</span>
                                                <div className="flex items-center gap-2 mt-2 text-emerald-100 text-xs">
                                                    <span className="capitalize">{dailyProblem.questions.difficulty}</span>
                                                    <span className="">|</span>
                                                    <span>{dailyProblem.questions.topic}</span>
                                                </div>
                                            </button>
                                            <button onClick={() => navigate(`/problems/${dailyProblem.question_id}`)}
                                                className="mt-3 w-full text-center py-1.5 rounded bg-white text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition-colors relative z-10">
                                                {dailyStreak.solvedToday ? 'Attempt Again →' : 'Solve Now →'}
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between mt-4 border-t border-border py-2">
                                            <div className="flex items-center gap-1.5"><FaFire className="text-muted-foreground w-3.5 h-3.5" /><span className="text-xs text-muted-foreground mt-0.5">Daily Challenge Solving Streak</span></div>
                                            <span className="text-base font-bold text-foreground">{dailyStreak.currentStreak}</span>
                                        </div>
                                        <button onClick={() => navigate('/dsa-problems')}
                                            className="mt-3 w-full text-center py-2 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors">
                                            Solve More Problems →
                                        </button>
                                    </>
                                ) : (
                                    <div className="py-6 text-center"><FiLoader className="text-lg mx-auto mb-1 animate-spin text-muted-foreground" /><p className="text-xs text-muted-foreground">Loading...</p></div>
                                )}
                            </div>

                            {/* CARD: User Productivity Stats */}
                            <div className="group bg-white border-l-4 border-emerald-600 rounded p-5 shadow-sm transition-all duration-300">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <FiBarChart2 className="text-emerald-600 w-4 h-4" />
                                        <h2 className="text-md font-semibold text-foreground mt-0.5">Your Stats</h2>
                                    </div>
                                </div>
                                <div className="space-y-0">
                                    <div className="flex items-center justify-between py-2.5 border-b border-border">
                                        <div className="flex items-center gap-2"><FiCheckCircle className="text-muted-foreground w-3.5 h-3.5" /><span className="text-sm text-muted-foreground">Total Solved</span></div>
                                        <span className="text-lg font-bold text-foreground">{userStats?.solvedStats?.total || 0}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-2.5 border-b border-border">
                                        <div className="flex items-center gap-2"><FaFire className="text-muted-foreground w-3.5 h-3.5" /><span className="text-sm text-muted-foreground">Submission Streak</span></div>
                                        <span className="text-lg font-bold text-foreground">{submissionStreak.currentStreak}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-2.5 border-b border-border">
                                        <div className="flex items-center gap-2"><FiCalendar className="text-muted-foreground w-3.5 h-3.5" /><span className="text-sm text-muted-foreground">Solved Today</span></div>
                                        <span className="text-lg font-bold text-foreground">{todaySolved}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-2.5">
                                        <div className="flex items-center gap-2"><FiTrendingUp className="text-muted-foreground w-3.5 h-3.5" /><span className="text-sm text-muted-foreground">Total Progress</span></div>
                                        <span className="text-lg font-bold text-foreground">{overallProgress}%</span>
                                    </div>
                                </div>
                            </div>

                            {/* CARD: Progress Visualization (Interactive Ring) */}
                            <div className="group bg-white border-l-4 border-emerald-600 rounded p-5 shadow-sm transition-all duration-300 flex flex-col">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <FiTrendingUp className="text-emerald-600 w-4 h-4" />
                                        <h2 className="text-md font-semibold text-foreground mt-0.5">Progress</h2>
                                    </div>
                                </div>
                                <div className="flex-1 flex items-center justify-center">
                                    {userStats && (
                                        <ProgressRing
                                            easy={userStats.solvedStats?.easy || 0}
                                            medium={userStats.solvedStats?.medium || 0}
                                            hard={userStats.solvedStats?.hard || 0}
                                            totalSolved={userStats.solvedStats?.total || 0}
                                            totalProblems={userStats.problemStats?.total || 1}
                                            easyTotal={userStats.problemStats?.easy || 0}
                                            mediumTotal={userStats.problemStats?.medium || 0}
                                            hardTotal={userStats.problemStats?.hard || 0}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* CARD: Earnings / Achievements */}
                            <div className="group bg-white border-l-4 border-emerald-600 rounded p-5 shadow-sm transition-all duration-300 flex flex-col">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <FiAward className="text-emerald-600 w-4 h-4" />
                                        <h2 className="text-md font-semibold text-foreground mt-0.5">Achievements</h2>
                                    </div>
                                    <button onClick={() => navigate('/profile?tab=achievements')}
                                        className="text-muted-foreground hover:text-emerald-600 transition-colors">
                                        <FiExternalLink className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex-1 flex items-center justify-center">
                                    <BadgeCarousel userId={user?.id} token={token} onViewAll={() => navigate('/profile?tab=achievements')} />
                                </div>
                            </div>
                        </div>

                        {/* Middle Row: Heatmap & Recent Activity */}
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                            {/* Heatmap Card */}
                            <div className="lg:col-span-3 bg-white border-l-4 border-emerald-600 rounded p-5 shadow-sm transition-all duration-300">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <FiActivity className="text-emerald-600 w-4 h-4" />
                                        <h2 className="text-md font-semibold text-foreground mt-0.5">Activity Calendar</h2>
                                    </div>
                                </div>
                                <ActivityCalendar userId={user?.id} token={token} />
                            </div>

                            {/* History List Card */}
                            <div className="lg:col-span-2 bg-white border-l-4 border-emerald-600 rounded p-5 shadow-sm transition-all duration-300">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <FiBookmark className="text-emerald-600 w-4 h-4" />
                                        <h2 className="text-md font-semibold text-foreground mt-0.5">Recent Submissions</h2>
                                    </div>
                                    <button onClick={() => navigate('/submissions')} className="text-muted-foreground hover:text-emerald-600 transition-colors">
                                        <FiExternalLink className="w-4 h-4" />
                                    </button>
                                </div>
                                {recentSubmissions.length > 0 ? (
                                    <div className="space-y-0.5">
                                        {recentSubmissions.slice(0, 3).map((sub, index, arr) => (
                                            <div key={sub.id} onClick={() => navigate(`/problems/${sub.questions?.id}`)} className={`flex items-center justify-between py-2.5 transition-colors cursor-pointer hover:bg-emerald-50/50 rounded px-1 ${index !== arr.length - 1 ? 'border-b border-border' : ''}`}>
                                                <div className="flex items-center gap-2 text-left min-w-0">
                                                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                                        {sub.status === 'Accepted' ? <FiCheck className="bg-green-600 text-white rounded-full p-0.5 w-6 h-6" /> : <FiX className="bg-red-600 text-white rounded-full p-0.5 w-6 h-6" />}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-medium text-foreground text-sm truncate">{sub.questions?.title || 'Problem'}</div>
                                                        <div className="flex items-center gap-1.5 text-xs capitalize">
                                                            <span className={getStatusColor(sub.status)}>{sub.status === 'Accepted' ? 'Accepted' : sub.status.includes('Wrong') ? 'Wrong Answer' : sub.status.includes('Time') ? 'Time Limit Exceeded' : sub.status.includes('Runtime') ? 'Runtime Error' : sub.status.includes('Compilation') ? 'Compilation Error' : sub.status}</span>
                                                            <span className="text-muted-foreground">•</span>
                                                            <span className={getDifficultyColor(sub.questions?.difficulty)}>{sub.questions?.difficulty}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-xs text-muted-foreground flex-shrink-0 ml-2">{new Date(sub.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <FiActivity className="text-3xl text-muted-foreground/30 mx-auto mb-2" />
                                        <p className="text-xs text-muted mb-3">No recent activity</p>
                                        <button onClick={() => navigate('/dsa-problems')} className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded transition-colors text-xs">Start Coding</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Section: System Designs */}
                    {IS_SYSTEM_DESIGN_COMING_SOON ? (
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold text-foreground flex items-center gap-2 underline decoration-emerald-600 underline-offset-4">
                                <FiLayers className="text-emerald-600 w-5 h-5" />
                                System Designs
                            </h2>
                            <div className="bg-white border-l-4 border-emerald-600 rounded p-12 text-center shadow-sm">
                                <FiLayers className="text-5xl text-emerald-600/30 mx-auto mb-4" />
                                <h2 className="text-xl font-semibold text-foreground mb-2">System Design Progress</h2>
                                <p className="text-muted-foreground max-w-md mx-auto">Content coming soon.</p>
                            </div>
                        </div>
                    ) : systemDesignProgressBreakdown ? (
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold text-foreground flex items-center gap-2 underline decoration-emerald-600 underline-offset-4">
                                <FiLayers className="text-emerald-600 w-5 h-5" />
                                System Designs
                            </h2>

                            {/* Mini Stats Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                <div className="bg-white border-l-4 border-emerald-600 rounded p-6 text-center shadow-sm transition-all duration-300">
                                    <div className="text-2xl font-bold text-foreground">{systemDesignProgressBreakdown.regularTopics.reduce((a, t) => a + t.completed, 0)}/{systemDesignProgressBreakdown.regularTopics.reduce((a, t) => a + t.total, 0)}</div>
                                    <div className="text-xs text-muted-foreground mt-1">Chapters Completed</div>
                                </div>
                                <div className="bg-white border-l-4 border-emerald-600 rounded p-6 text-center shadow-sm transition-all duration-300">
                                    <div className="text-2xl font-bold text-foreground">{systemDesignProgressBreakdown.interviewDesigns.completed}/{systemDesignProgressBreakdown.interviewDesigns.total}</div>
                                    <div className="text-xs text-muted-foreground mt-1">Designs Completed</div>
                                </div>
                                <div className="bg-white border-l-4 border-emerald-600 rounded p-6 text-center shadow-sm transition-all duration-300">
                                    <div className="text-2xl font-bold text-foreground">{systemDesignStats?.completionPercentage || 0}%</div>
                                    <div className="text-xs text-muted-foreground mt-1">Overall Progress</div>
                                </div>
                            </div>

                            {/* Detailed Progress Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                                {/* Regular Topics Bars */}
                                <div className="lg:col-span-3 bg-white border-l-4 border-emerald-600 rounded p-5 shadow-sm transition-all duration-300">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <FiBox className="text-emerald-600 w-4 h-4" />
                                            <h2 className="text-md font-semibold text-foreground mt-0.5">Topics Progress</h2>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        {systemDesignProgressBreakdown.regularTopics.map((t) => (
                                            <div key={t.id}>
                                                <div className="flex justify-between items-center mb-1.5">
                                                    <span className="font-medium text-foreground text-sm">{t.title}</span>
                                                    <span className="text-muted-foreground text-xs">{t.completed}/{t.total}</span>
                                                </div>
                                                <div className="w-full bg-accent rounded-full h-1">
                                                    <div className="h-1 rounded-full bg-emerald-600 transition-all duration-700 ease-out" style={{ width: `${t.percentage}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Completed Designs List */}
                                <div className="lg:col-span-2 bg-white border-l-4 border-emerald-600 rounded p-5 shadow-sm transition-all duration-300 h-full flex flex-col">
                                    <div className="flex items-center gap-2 mb-4">
                                        <FiCheckSquare className="text-emerald-600 w-4 h-4" />
                                        <h2 className="text-md font-semibold text-foreground mt-0.5">Completed Designs</h2>
                                    </div>
                                    {systemDesignProgressBreakdown.interviewDesigns.completedList.length > 0 ? (
                                        <div className="space-y-0.5">
                                            {systemDesignProgressBreakdown.interviewDesigns.completedList.map((i, index, arr) => (
                                                <div key={i.id} onClick={() => navigate(`/system-design/view/${i.id}`)} className={`flex items-center gap-2 py-2.5 px-1 rounded transition-colors cursor-pointer hover:bg-emerald-50/50 ${index !== arr.length - 1 ? 'border-b border-border' : ''}`}>
                                                    <FiCheck className="bg-green-600 text-white p-0.5 rounded-full w-4 h-4 flex-shrink-0" />
                                                    <span className="text-foreground text-sm text-left truncate font-medium hover:underline">{i.title}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex-1 flex flex-col items-center justify-center py-10 text-muted-foreground">
                                            <FiLayers className="text-2xl mb-2 opacity-40" />
                                            <p className="text-xs">No designs completed yet</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </main>

            {/* Modal: New Achievement Celebration */}
            {showDashboardBadgeNotification && pendingBadges.length > 0 && (
                <>
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={closeDashboardBadgeNotification} />
                    <div className="fixed inset-0 flex items-center justify-center z-50">
                        <div className="relative bg-white border-2 border-emerald-600 rounded shadow-xl px-8 py-6 max-w-md mx-4 text-center">
                            <button onClick={closeDashboardBadgeNotification} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"><FiX className="w-5 h-5" /></button>
                            <div className="my-4 flex justify-center">
                                {pendingBadges.length === 1 ? (
                                    <BadgeImage badgeId={pendingBadges[0].badge_types?.id} imagePath={pendingBadges[0].badge_types?.image_path} badgeTitle={pendingBadges[0].badge_types?.badge_title} badgeType={pendingBadges[0].badge_types?.badge_name} tier={pendingBadges[0].badge_types?.badge_tier} isEarned={true} size="xl" />
                                ) : <FaStar className="text-amber-400 text-6xl" />}
                            </div>
                            <h3 className="text-2xl font-bold text-foreground mb-2">New Achievement{pendingBadges.length > 1 ? 's' : ''}!</h3>
                            <p className="text-muted-foreground text-sm">You earned {pendingBadges.length} new badge{pendingBadges.length > 1 ? 's' : ''}!</p>
                        </div>
                    </div>
                </>
            )}

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * SKELETON LOADER
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component DashboardSkeleton
 * @description A placeholder UI that mimics the Dashboard layout while data is loading.
 *              Uses animated pulse effects to improve perceived performance.
 */
const DashboardSkeleton = ({ navigate }) => (
    <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
        <Navbar navigate={navigate} />
        <main className="flex-1 py-16">
            <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50">
                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-3">
                    <div className="flex items-center justify-between">
                        <div className="h-8 w-64 bg-emerald-100 rounded animate-pulse" />
                        <div className="hidden sm:flex gap-2">{[1, 2].map(i => <div key={i} className="h-8 w-24 bg-emerald-100 rounded animate-pulse" />)}</div>
                    </div>
                </div>
            </section>
            <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-8 space-y-6">
                <div className="h-6 w-64 bg-emerald-100 rounded animate-pulse" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white border-l-4 border-emerald-600 rounded p-5 space-y-3 shadow-sm">
                            <div className="h-4 w-1/2 bg-gray-100 rounded animate-pulse" />
                            <div className="h-20 w-full bg-gray-50 rounded animate-pulse" />
                            <div className="h-6 w-full bg-gray-50 rounded animate-pulse" />
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                    <div className="lg:col-span-3 bg-white border-l-4 border-emerald-600 rounded p-5 space-y-3 shadow-sm">
                        <div className="h-4 w-1/4 bg-gray-100 rounded animate-pulse" />
                        <div className="h-24 w-full bg-gray-50 rounded animate-pulse" />
                    </div>
                    <div className="lg:col-span-2 bg-white border-l-4 border-emerald-600 rounded p-5 space-y-2 shadow-sm">
                        <div className="h-4 w-1/3 bg-gray-100 rounded animate-pulse" />
                        {[1, 2, 3].map(i => <div key={i} className="h-10 w-full bg-gray-50 rounded animate-pulse" />)}
                    </div>
                </div>
            </div>
        </main>
    </div>
);

export default Dashboard;